import React, { useEffect, useState } from "react";
import { Container, Form, Button, Table, Spinner, Alert, Row, Col, Card } from "react-bootstrap";
import API from "../services/api";

const defaultPolicy = {
  enforceOfficeLocation: false,
  officeRadiusMeters: 200,
  officeAddress: "",
  officeLocation: { lat: "", lng: "" },
};

const prettyStatus = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "present") return "Present";
  if (s === "absent") return "Absent";
  if (s === "halfday" || s === "half-day") return "Half Day";
  if (s === "outside-office") return "Outside Office";
  return status || "-";
};

const readableLocation = (row) => {
  const a =
    row?.currentTracking?.lastAddress ||
    row?.punchOut?.address ||
    row?.punchIn?.address ||
    row?.address ||
    "";
  if (a) return a;
  const lat =
    row?.currentTracking?.lastLocation?.lat ??
    row?.punchOut?.location?.lat ??
    row?.punchIn?.location?.lat ??
    row?.location?.lat;
  const lng =
    row?.currentTracking?.lastLocation?.lng ??
    row?.punchOut?.location?.lng ??
    row?.punchIn?.location?.lng ??
    row?.location?.lng;
  if (typeof lat === "number" && typeof lng === "number") {
    return "Location captured";
  }
  return "-";
};

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`
    );
    if (!res.ok) return "";
    const data = await res.json();
    return data?.display_name || "";
  } catch {
    return "";
  }
};

const AdminAttendancePage = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [policy, setPolicy] = useState(defaultPolicy);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyLocLoading, setPolicyLocLoading] = useState(false);

  const handleFetch = async () => {
    try {
      if (!from || !to) return setMessage("Please select both dates");
      setLoading(true);
      const res = await API.get(`/admin/attendance-summary?from=${from}&to=${to}`);
      setRecords(res.data || []);
      setMessage("");
    } catch (err) {
      console.error(err);
      setMessage("Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      if (!from || !to) return setMessage("Please select both dates");

      const res = await API.get(`/admin/attendance/export?from=${from}&to=${to}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance_${from}_to_${to}.csv`);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage("");
    } catch (err) {
      console.error(err);
      setMessage("Failed to download CSV");
    }
  };

  const loadPolicy = async () => {
    try {
      const res = await API.get("/admin/attendance-policy");
      const data = res.data || {};
      setPolicy({
        enforceOfficeLocation: Boolean(data.enforceOfficeLocation),
        officeRadiusMeters: Number(data.officeRadiusMeters || 200),
        officeAddress: data.officeAddress || "",
        officeLocation: {
          lat: data.officeLocation?.lat ?? "",
          lng: data.officeLocation?.lng ?? "",
        },
      });
    } catch (err) {
      console.error("Failed to load policy", err);
      setMessage("Failed to load attendance policy");
    }
  };

  const savePolicy = async () => {
    try {
      setSavingPolicy(true);
      const payload = {
        enforceOfficeLocation: policy.enforceOfficeLocation,
        officeRadiusMeters: Number(policy.officeRadiusMeters || 200),
        officeAddress: policy.officeAddress,
        officeLocation:
          policy.officeLocation.lat !== "" && policy.officeLocation.lng !== ""
            ? {
                lat: Number(policy.officeLocation.lat),
                lng: Number(policy.officeLocation.lng),
              }
            : null,
      };
      await API.put("/admin/attendance-policy", payload);
      setMessage("Attendance policy saved");
      await loadPolicy();
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to save attendance policy");
    } finally {
      setSavingPolicy(false);
    }
  };

  useEffect(() => {
    loadPolicy();
  }, []);

  const useCurrentOfficeLocation = () => {
    if (!navigator?.geolocation) {
      setMessage("Geolocation not supported in browser");
      return;
    }
    setPolicyLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude).toFixed(7);
        const lng = Number(pos.coords.longitude).toFixed(7);
        const address = await reverseGeocode(lat, lng);
        setPolicy((prev) => ({
          ...prev,
          officeLocation: { lat, lng },
          officeAddress: address || prev.officeAddress,
        }));
        setPolicyLocLoading(false);
      },
      () => {
        setPolicyLocLoading(false);
        setMessage("Unable to fetch current location");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  return (
    <Container className="mt-4">
      <h4>Attendance Summary</h4>

      {message && <Alert variant={message.toLowerCase().includes("failed") ? "danger" : "success"}>{message}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <h6 className="mb-3">Office Attendance Policy</h6>
          <Row className="g-3">
            <Col md={3}>
              <Form.Check
                type="switch"
                label="Enforce Office Geofence"
                checked={policy.enforceOfficeLocation}
                onChange={(e) => setPolicy((prev) => ({ ...prev, enforceOfficeLocation: e.target.checked }))}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Allowed Radius (meters)</Form.Label>
              <Form.Control
                type="number"
                min={10}
                value={policy.officeRadiusMeters}
                onChange={(e) => setPolicy((prev) => ({ ...prev, officeRadiusMeters: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Office Pin A</Form.Label>
              <Form.Control
                type="number"
                value={policy.officeLocation.lat}
                onChange={(e) =>
                  setPolicy((prev) => ({
                    ...prev,
                    officeLocation: { ...prev.officeLocation, lat: e.target.value },
                  }))
                }
              />
            </Col>
            <Col md={3}>
              <Form.Label>Office Pin B</Form.Label>
              <Form.Control
                type="number"
                value={policy.officeLocation.lng}
                onChange={(e) =>
                  setPolicy((prev) => ({
                    ...prev,
                    officeLocation: { ...prev.officeLocation, lng: e.target.value },
                  }))
                }
              />
            </Col>
            <Col md={9}>
              <Form.Label>Office Address</Form.Label>
              <Form.Control
                value={policy.officeAddress}
                onChange={(e) => setPolicy((prev) => ({ ...prev, officeAddress: e.target.value }))}
              />
            </Col>
            <Col md={3} className="d-flex align-items-end gap-2">
              <Button variant="outline-secondary" onClick={useCurrentOfficeLocation} disabled={policyLocLoading}>
                {policyLocLoading ? "Fetching..." : "Use Current Location"}
              </Button>
              <Button variant="primary" onClick={savePolicy} disabled={savingPolicy}>
                {savingPolicy ? "Saving..." : "Save Policy"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Form className="d-flex gap-3 my-3 flex-wrap">
        <Form.Group>
          <Form.Label>From Date</Form.Label>
          <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>To Date</Form.Label>
          <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Form.Group>
        <div className="align-self-end">
          <Button variant="primary" onClick={handleFetch} className="me-2">
            Filter
          </Button>
          <Button variant="success" onClick={handleDownload}>
            Download CSV
          </Button>
        </div>
      </Form>

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Mobile</th>
              <th>Status</th>
              <th>Mode</th>
              <th>Meeting</th>
              <th>Location</th>
              <th>Office Distance (m)</th>
              <th>Punch In</th>
              <th>Punch Out</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center">
                  No records
                </td>
              </tr>
            ) : (
              records.map((r, i) => (
                <tr key={i}>
                  <td>{r.employee?.fullName || "Unknown"}</td>
                  <td>{r.employee?.mobile || "N/A"}</td>
                  <td>{prettyStatus(r.status)}</td>
                  <td>{r.attendanceModeSnapshot || "office"}</td>
                  <td>{r.currentTracking?.meetingMode ? "On" : "Off"}</td>
                  <td>{readableLocation(r)}</td>
                  <td>
                    {typeof r.officeValidation?.distanceMeters === "number"
                      ? `${r.officeValidation.distanceMeters}${r.officeValidation?.withinRange === false ? " (outside)" : ""}`
                      : "-"}
                  </td>
                  <td>{r.punchIn?.time ? new Date(r.punchIn.time).toLocaleTimeString() : "-"}</td>
                  <td>{r.punchOut?.time ? new Date(r.punchOut.time).toLocaleTimeString() : "-"}</td>
                  <td>{new Date(r.date).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default AdminAttendancePage;
